/**
 * Utility functions for authentication
 */
import permissionStore, {
  hasPermission,
  canChangeJobStatus,
  processPermissions as processUserPermissions,
  getStoredPermissions as getUserPermissions
} from './permissionStore';

// Re-export utility functions from permissionStore
export { hasPermission, canChangeJobStatus, processUserPermissions, getUserPermissions };

/**
 * Check if a JWT token is expired
 * @param {string} token - The JWT token to check
 * @returns {boolean} - True if token is expired or invalid, false otherwise
 */
export const isTokenExpired = (token) => {
  if (!token) {
    return true;
  }
  
  try {
    // Get the payload part of the JWT (second part)
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const { exp } = JSON.parse(jsonPayload);
    
    // Check if expiration time is in the past
    return exp * 1000 < Date.now();
  } catch (error) {
    // console.error('Error checking token expiration:', error); // Removed log
    return true; // Consider invalid tokens as expired
  }
};

/**
 * Verify token validity by making a lightweight API call
 * @returns {Promise<boolean>} - Promise resolving to true if token is valid, false otherwise
 */
export const verifyToken = async () => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    return false;
  }
  
  // First check if token is expired based on its payload
  if (isTokenExpired(token)) {
    // console.log('Token is expired, clearing storage'); // Removed log
    
    // Clear authentication data from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Also clear backup data from sessionStorage
    sessionStorage.removeItem('backup_token');
    sessionStorage.removeItem('backup_user');
    
    return false;
  }
  
  try {
    // Make a lightweight API call to verify token on the server
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/verify`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    // Only an explicit auth rejection invalidates the session. Server errors
    // (5xx) don't prove the token is bad.
    if (response.status === 401 || response.status === 403) {
      return false;
    }
    return true;
  } catch (error) {
    // Network error: the backend is down or still starting. The token itself
    // is locally valid (expiry already checked above), so keep the session —
    // wiping storage here destroyed "Remember me for 30 days" whenever the
    // app loaded before the backend was reachable.
    return true;
  }
};

/**
 * Log out the user by clearing localStorage and sessionStorage, then redirecting to login
 */
export const logout = () => {
  // console.log('Logging out user...'); // Removed log
  
  // Clear authentication data from localStorage
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('userInfo');
  
  // Also clear backup data from sessionStorage
  sessionStorage.removeItem('backup_token');
  sessionStorage.removeItem('backup_user');
  sessionStorage.removeItem('wk_act_as'); // acting as a partner company ends with the login
  
  // Clear any other potential auth-related items
  localStorage.removeItem('audioContextUnlocked');
  sessionStorage.removeItem('socketEvents');
  sessionStorage.removeItem('showNotification');
  sessionStorage.removeItem('pendingNotificationSound');
  // Owner Console unlock and the inactivity clock belong to the session that just ended
  sessionStorage.removeItem('ownerToken');
  sessionStorage.removeItem('ownerTokenExpiresAt');
  localStorage.removeItem('wk_last_activity');
  localStorage.removeItem('wk_remember_me');

  // Clear permissions
  permissionStore.clearPermissions();
  
  // console.log('Storage cleared, redirecting to login page'); // Removed log
  
   // Redirect to login page
   window.location.href = '/login';
 };
 
 /**
  * Get the authorization header with the JWT token.
  * @returns {object} - Headers object with Authorization and Content-Type.
  */
 export const getAuthHeader = () => {
   const token = localStorage.getItem('token');
   const headers = {
     'Content-Type': 'application/json',
   };
   if (token) {
     headers['Authorization'] = `Bearer ${token}`;
   }
   return headers;
 };
 
 /**
  * Constructs the full URL for a document given its path.
  * Enhanced to handle different file types and directory structures.
  * @param {string} filePath - The path to the document.
  * @returns {string} - The full URL to the document.
  */
 export const getDocumentUrl = (filePath) => {
   // Only stored server paths are linkable; File objects and anything else
   // non-string must never crash the caller
   if (!filePath || typeof filePath !== 'string') return '';
   const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';

   // filePath might be 'background_checks/file.pdf', 'insurance/file.pdf', or just 'file.pdf' (for older uploads like W9/COI)
   // The server serves '/uploads/*' statically.
   const documentPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;

   // Handle different file types based on path structure
   let url;
   if (documentPath.startsWith('uploads/')) {
       // Path already includes 'uploads/' prefix (like profile pictures)
       url = `${baseUrl}/${documentPath}`;
   } else if (documentPath.startsWith('background_checks/')) {
       // For background check files, use the path as is
       url = `${baseUrl}/uploads/${documentPath}`;
   } else if (documentPath.startsWith('insurance/')) {
       // For insurance documents (COI files), use the path as is
       url = `${baseUrl}/uploads/${documentPath}`;
   } else {
       // For all other files, assume they're in the applications subdirectory
       url = `${baseUrl}/uploads/applications/${documentPath}`;
   }

   // /uploads/partners and /uploads/profile are public; everything else is
   // auth-gated on the server. Links/img tags can't send headers, so pass the
   // JWT as a query param for the gated paths.
   const isPublicPath = /\/uploads\/(partners|profile)\//.test(url);
   if (!isPublicPath) {
     const token = localStorage.getItem('token');
     if (token) {
       url += `${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
     }
   }
   return url;
 };
